import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useBreakpoint, useI18n, View} from "shared/base"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import Link from "@kaspernj/api-maker/build/link"
import OptionsButton from "components/school-classes/options-button"
import React from "react"
import Routes from "shared/routes"
import {SchoolClass} from "models"
import SubscriptionBadge from "components/school-classes/card/subscription-badge"

/**
 * @typedef {object} RoutesUserDashboardSchoolClassRowProps
 * @property {unknown} lastRow
 * @property {unknown} schoolClass
 */

/** @typedef {object} RoutesUserDashboardSchoolClassRowState */

/**
 * @extends {ShapeComponent<RoutesUserDashboardSchoolClassRowProps, RoutesUserDashboardSchoolClassRowState>}
 */
class RoutesUserDashboardSchoolClassRow extends ShapeComponent {
  static propTypes = propTypesExact({
    lastRow: PropTypes.bool.isRequired,
    schoolClass: PropTypes.instanceOf(SchoolClass).isRequired
  })

  setup() {
    const {t} = useI18n({namespace: "js.routes.user.my_classrooms_route"})

    this.t = t
    this.breakpoint = useBreakpoint()
    this.xsUp = this.breakpoint.xsUp
    this.xsDown = this.breakpoint.xsDown
    this.smUp = this.breakpoint.smUp
    this.smDown = this.breakpoint.smDown
    this.mdUp = this.breakpoint.mdUp
    this.mdDown = this.breakpoint.mdDown
    this.lgUp = this.breakpoint.lgUp
    this.lgDown = this.breakpoint.lgDown
    this.xlUp = this.breakpoint.xlUp
    this.xlDown = this.breakpoint.xlDown
    this.xxlUp = this.breakpoint.xxlUp
    this.xxlDown = this.breakpoint.xxlDown
    this.windowWidth = this.breakpoint.windowWidth
    this.windowHeight = this.breakpoint.windowHeight
  }

  render() {
    const {t} = this.tt
    const {lastRow, schoolClass} = this.p

    return (
      <View
        dataSet={this.cache(`schoolClassCardDataSet${schoolClass.id()}`, {class: "school-class-card", schoolClassId: schoolClass.id()})}
        key={schoolClass.id()}
        style={this.cache(`schoolClassCardStyle${schoolClass.id()}`, {
          flexDirection: "row",
          alignItems: "center",
          marginBottom: lastRow ? undefined : 8,
          padding: 17,
          backgroundColor: "#fff",
          borderRadius: 24
        }, [lastRow])}
      >
        <Text style={this.classStepTextStyle ||= {width: 54, color: "#5253ff", fontSize: 24, fontWeight: 600}}>
          {schoolClass.classStep()?.hasPrimarySchoolStep() &&
            t(".class_step_with_ending", {class_step_number: schoolClass.classStep().primarySchoolStep()})
          }
        </Text>
        <View>
          <View style={{flexDirection: "row", alignItems: "center"}}>
            {schoolClass.hasInterpretedLegalName() &&
              <Text style={this.schoolClassLegalNameStyle ||= {color: "#2c2c96", fontSize: 20, fontWeight: "bold", lineHeight: "120%"}}>
                {schoolClass.interpretedLegalName()}
              </Text>
            }
            {schoolClass.hasInterpretedLegalName() && schoolClass.hasInterpretedName() &&
              <Text
                style={this.schoolClassMiddleOfNameStyle ||= {
                  marginLeft: 5,
                  marginRight: 5,
                  color: "#2c2c96",
                  fontSize: 20,
                  fontWeight: "bold",
                  lineHeight: "120%"
                }}
              >
                -
              </Text>
            }
            {schoolClass.hasInterpretedName() &&
              <Text style={this.schoolClassNameStyle ||= {color: "#2c2c96", fontSize: 20, fontWeight: "bold", lineHeight: "120%"}}>
                {schoolClass.interpretedName()}
              </Text>
            }
            {schoolClass.activeSubscriptions().loaded().length == 0 &&
              <View style={this.subscriptionBadgeViewStyle ||= {marginLeft: 10}}>
                <SubscriptionBadge />
              </View>
            }
            {schoolClass.activeSubscriptions().loaded().map((subscription) =>
              <View key={subscription.id()} style={this.subscriptionBadgeViewStyle ||= {marginLeft: 10}}>
                <SubscriptionBadge subscription={subscription} />
              </View>
            )}
          </View>
          {schoolClass.teachers().loaded().map((teacher, teacherIndex) =>
            <Text key={teacher.id()} style={this.teacherNameTextStyle ||= {color: "#87a2d5", lineHeight: "120%"}}>
              {teacher.name()}{teacherIndex + 1 < schoolClass.teachers().loaded().length && ", "}
            </Text>
          )}
        </View>
        <View style={this.playIconContainerStyle ||= {marginLeft: "auto", marginRight: 10}}>
          <OptionsButton schoolClass={schoolClass} />
        </View>
        <Link target="_blank" to={Routes.userSchoolClassClassSpacePath(schoolClass.id())}>
          <View
            style={this.planIconContainerStyle ||= {
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              paddingLeft: 5,
              backgroundColor: "#5253ff",
              borderRadius: "50%"
            }}
          >
            <Icon name="play" size={20} style={this.playIconStyle ||= {color: "#fff"}} />
          </View>
        </Link>
      </View>
    )
  }
}

export default memo(shapeComponent(RoutesUserDashboardSchoolClassRow))
